import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  IoCheckmarkCircle,
  IoGitCompareOutline,
  IoLockClosedOutline,
  IoSaveOutline,
  IoSearchOutline,
  IoTrashOutline,
} from 'react-icons/io5';

import {
  Notice,
  Section,
  VersionList,
  badge,
  danger,
  draftOf,
  errorText,
  formatDate,
  input,
  liveOf,
  mono,
  nextVersion,
  primary,
  secondary,
} from './PipelineSettingsPanel';
import { confirmDialog } from '../../components/ConfirmDialog';
import { useAccess } from '../../services/access';
import { configApi, pipelineApi } from '../../services/api';

// Admin → Configuration → Prompts. Every system prompt the platform sends,
// grouped by feature area. Editable areas go draft → check → compare →
// publish; chat prompts are view-only. A publish reaches every process on its
// next call, and records keep the version they were made under.

const AREA_LABEL = {
  meetings: 'Meetings',
  mail: 'Mail',
  assistant: 'Assistant',
  chat: 'Chat',
};

const WHERE = {
  meetings: 'Every meeting extracted from now on',
  mail: 'Mail processed from now on',
  assistant: 'Every call from now on',
};

const fail = (error) => toast.error(errorText(error));

// Mirrors the server check: guardrail sentences (case-insensitive) and the
// {placeholders} the code fills.
const guardrails = (spec, body) => {
  const text = body.toLowerCase();
  return [
    ...spec.markers.map((m) => ({
      label: m,
      ok: text.includes(m.toLowerCase()),
    })),
    ...spec.placeholders.map((p) => ({
      label: `{${p}}`,
      ok: body.includes(`{${p}}`),
    })),
  ];
};

const State = ({ live, draft, editable }) => (
  <span className="flex shrink-0 items-center gap-1">
    {draft && (
      <span className={`${badge} bg-sky-500/10 text-sky-300`}>
        draft {draft.version}
      </span>
    )}
    {live ? (
      <span className={`${badge} bg-emerald-500/10 text-emerald-300`}>
        live {live.version}
      </span>
    ) : (
      <span className={`${badge} text-zinc-400`}>
        {editable ? 'in code' : 'view only'}
      </span>
    )}
  </span>
);

const Rail = ({ specs, areas, versions, selected, onSelect }) => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = specs.filter(
    (s) =>
      !q ||
      s.title.toLowerCase().includes(q) ||
      s.key.toLowerCase().includes(q) ||
      s.purpose.toLowerCase().includes(q),
  );
  return (
    <nav aria-label="Prompts" className="space-y-4">
      <label className="relative block">
        <span className="sr-only">Filter prompts</span>
        <IoSearchOutline className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          className={`${input} pl-9`}
          placeholder="Filter prompts"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {areas.map((area) => {
        const group = shown.filter((s) => s.area === area);
        if (!group.length) return null;
        return (
          <div key={area}>
            <h4 className="mb-1 flex items-center gap-1.5 px-2 text-xs font-medium text-zinc-400">
              {AREA_LABEL[area] || area}
              {!group[0].editable && (
                <>
                  <IoLockClosedOutline aria-hidden /> view only
                </>
              )}
            </h4>
            <ul className="space-y-0.5">
              {group.map((s) => (
                <li key={s.key}>
                  <button
                    onClick={() => onSelect(s.key)}
                    aria-current={s.key === selected ? 'true' : undefined}
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 ${
                      s.key === selected
                        ? 'bg-indigo-600/15 text-indigo-200'
                        : 'text-zinc-300 hover:bg-zinc-800/70'
                    }`}
                  >
                    <span className="min-w-0 truncate">{s.title}</span>
                    <State
                      live={liveOf(versions, 'prompt_key', s.key)}
                      draft={draftOf(versions, 'prompt_key', s.key)}
                      editable={s.editable}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {!shown.length && (
        <p className="px-2 text-sm text-zinc-400">No prompt matches.</p>
      )}
    </nav>
  );
};

const Editor = ({ token, spec, versions, canManage, reload }) => {
  const { key } = spec;
  const docs = versions.filter((v) => v.prompt_key === key);
  const live = liveOf(versions, 'prompt_key', key);
  const draft = draftOf(versions, 'prompt_key', key);
  const [body, setBody] = useState('');
  const [notes, setNotes] = useState('');
  const [check, setCheck] = useState(null);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);

  // The editor shows the draft, else what is running.
  useEffect(() => {
    let stale = false;
    setCheck(null);
    setDiff(null);
    setNotes(draft?.notes || '');
    if (draft || live) {
      setBody((draft || live).body);
      return undefined;
    }
    setBody('');
    pipelineApi
      .promptDefault(token, key)
      .then((res) => !stale && setBody(res.body))
      .catch(fail);
    return () => {
      stale = true;
    };
  }, [token, key, draft, live]);

  const rails = useMemo(() => guardrails(spec, body), [spec, body]);
  const dropped = rails.filter((r) => !r.ok);
  const editing = canManage && spec.editable && Boolean(draft);

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const asDraft = () => ({
    prompt_key: key,
    version: draft.version,
    body,
    scope: 'GLOBAL',
    notes: notes || null,
  });

  const start = () =>
    run(async () => {
      await pipelineApi.createPromptDraft(token, {
        prompt_key: key,
        version: nextVersion(versions, 'prompt_key', key),
        body,
        scope: 'GLOBAL',
      });
      await reload();
    }, 'Draft created');

  const save = () =>
    run(async () => {
      await pipelineApi.savePromptDraft(token, draft._id, {
        body,
        notes: notes || null,
      });
      await reload();
    }, 'Draft saved');

  const runCheck = () =>
    run(async () =>
      setCheck(await pipelineApi.previewPrompt(token, asDraft())),
    );

  const compare = () =>
    run(async () =>
      setDiff(await pipelineApi.diffPrompt(token, live._id, asDraft())),
    );

  const publish = () =>
    run(async () => {
      const ok = await confirmDialog({
        title: `Publish ${key}@${draft.version}?`,
        message: `${WHERE[spec.area] || 'Every call from now on'} uses this prompt, in every process within seconds. Records already made keep the prompt they were made with.`,
        confirmLabel: 'Publish',
      });
      if (!ok) return;
      await pipelineApi.publishPrompt(token, asDraft());
      await reload();
      toast.success('Published');
    });

  const discard = () =>
    run(async () => {
      const ok = await confirmDialog({
        title: 'Discard this draft?',
        message:
          'The draft text is deleted. Published versions are not affected.',
        confirmLabel: 'Discard',
        danger: true,
      });
      if (!ok) return;
      await pipelineApi.deletePromptDraft(token, draft._id);
      await reload();
    });

  const archive = (ref) =>
    run(async () => {
      const ok = await confirmDialog({
        title: `Archive ${ref}?`,
        message:
          'If this is the live version, the in-code prompt runs again. Stored records keep pointing at it.',
        confirmLabel: 'Archive',
        danger: true,
      });
      if (!ok) return;
      await pipelineApi.archivePrompt(token, ref);
      await reload();
    });

  return (
    <div className="min-w-0 space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-zinc-100">{spec.title}</h3>
          <p className="text-sm text-zinc-400">{spec.purpose}</p>
          <p className="mt-1 text-xs text-zinc-400">
            <span className={mono}>{key}</span> ·{' '}
            {live
              ? `live ${live._id}, published ${formatDate(live.published_at)}`
              : 'running the in-code text'}
          </p>
        </div>
        {canManage && spec.editable && !draft && (
          <button className={primary} disabled={busy || !body} onClick={start}>
            Start a draft
          </button>
        )}
      </header>

      {!spec.editable && (
        <Notice tone="zinc">
          Chat prompts are view-only: they are long, shared by every model, and
          the riskiest to change. Edit them in code.
        </Notice>
      )}
      {spec.legacy_override && (
        <Notice>
          A prompt saved through the retired /systemPrompt API replaces this
          text in chat. It stays until its rows are removed from the
          systemPrompts collection.
        </Notice>
      )}
      {draft && (
        <p className="text-sm text-sky-300">
          Editing draft {draft._id}. Nothing uses it until it is published.
        </p>
      )}

      {rails.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-zinc-400">Must keep</span>
          {rails.map((r) => (
            <span
              key={r.label}
              className={`${badge} ${mono} ${
                r.ok
                  ? 'bg-zinc-800 text-zinc-300'
                  : 'bg-red-500/15 text-red-300'
              }`}
              title={r.ok ? 'Present' : 'Missing: publishing will be refused'}
            >
              {r.label}
            </span>
          ))}
        </div>
      )}

      <div>
        <textarea
          aria-label={`${spec.title} prompt text`}
          className={`${input} ${mono} h-[28rem] resize-y leading-5`}
          value={body}
          readOnly={!editing}
          spellCheck={false}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="mt-1 text-xs tabular-nums text-zinc-400">
          {body.length.toLocaleString()} characters · about{' '}
          {Math.max(1, Math.round(body.length / 4)).toLocaleString()} tokens per
          call
          {dropped.length > 0 && (
            <span className="text-red-300">
              {' '}
              · missing {dropped.map((d) => d.label).join(', ')}
            </span>
          )}
        </p>
      </div>

      {editing && (
        <div className="space-y-3">
          <input
            className={input}
            placeholder="What changed and why (shown in the version list)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <button className={secondary} disabled={busy} onClick={save}>
              <IoSaveOutline aria-hidden /> Save draft
            </button>
            <button className={secondary} disabled={busy} onClick={runCheck}>
              <IoCheckmarkCircle aria-hidden /> Check
            </button>
            {live && (
              <button className={secondary} disabled={busy} onClick={compare}>
                <IoGitCompareOutline aria-hidden /> Compare with live
              </button>
            )}
            <button
              className={primary}
              disabled={busy || dropped.length > 0}
              title={
                dropped.length ? 'Restore the missing text first' : undefined
              }
              onClick={publish}
            >
              Publish
            </button>
            <button className={danger} disabled={busy} onClick={discard}>
              <IoTrashOutline aria-hidden /> Discard
            </button>
          </div>
        </div>
      )}

      {check && (
        <div className="space-y-2">
          {check.format_problem && <Notice>{check.format_problem}</Notice>}
          {check.missing_markers.length > 0 && (
            <Notice>
              Missing required text: <b>{check.missing_markers.join(', ')}</b>.
              Publishing is refused until it is back.
            </Notice>
          )}
          {check.publishable && (
            <p className="text-sm text-emerald-400">
              Ready to publish: every required sentence and placeholder is
              present.
            </p>
          )}
        </div>
      )}

      {diff && (
        <div>
          <p className="mb-1 text-sm text-zinc-400">
            {diff.added} line(s) added, {diff.removed} removed vs{' '}
            {diff.from_ref}
          </p>
          <pre className="max-h-80 overflow-auto rounded-md bg-zinc-900 p-3 text-xs">
            {diff.diff.map((line, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={
                  line.startsWith('+')
                    ? 'text-emerald-400'
                    : line.startsWith('-')
                      ? 'text-red-400'
                      : 'text-zinc-400'
                }
              >
                {line}
              </div>
            ))}
          </pre>
        </div>
      )}

      {spec.editable && (
        <Section title="Versions">
          <VersionList docs={docs} canManage={canManage} onArchive={archive} />
        </Section>
      )}
    </div>
  );
};

export default function PromptsPanel({ userIdToken, initialKey }) {
  const access = useAccess();
  const canManage = access.can('admin:config:manage');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(initialKey || null);

  const load = useCallback(async () => {
    setData(await configApi.prompts(userIdToken));
  }, [userIdToken]);

  useEffect(() => {
    if (userIdToken) load().catch((e) => setError(errorText(e)));
  }, [userIdToken, load]);

  if (error) return <Notice>{error}</Notice>;
  if (!data) return <p className="text-sm text-zinc-400">Loading prompts…</p>;

  const spec =
    data.specs.find((s) => s.key === selected) ||
    data.specs.find((s) => s.editable) ||
    data.specs[0];

  return (
    <div className="grid gap-6 md:grid-cols-[260px_minmax(0,1fr)]">
      <Rail
        specs={data.specs}
        areas={data.areas}
        versions={data.versions}
        selected={spec.key}
        onSelect={setSelected}
      />
      <Editor
        key={spec.key}
        token={userIdToken}
        spec={spec}
        versions={data.versions}
        canManage={canManage}
        reload={load}
      />
    </div>
  );
}
